import { Alert, LinearProgress } from "@mui/material";
import { ReactNode, useMemo } from "react";

import { EntityHelper } from "@/common/helpers/entity";
import { UseApiRequestResult } from "@/common/hooks/api/useApiRequest";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { BaseEntityDto, DataGrantPermission, EntityType } from "@/core/api/generated/v1";

import EntityForbiddenAlert from "../../AppAlerts/EntityForbiddenAlert";
import EntityNotFoundAlert from "../../AppAlerts/EntityNotFoundAlert";

export interface BaseEntityViewOwnProps<TEntity extends BaseEntityDto> {
  entityType: EntityType;
  entityId: string | null | undefined;
  entity: TEntity | null | undefined;
  entityRequest: UseApiRequestResult<any, any, any, any, any> | null | undefined; // use any as we don't care about generic arguments
  /** Whether initialization is in progress and view shouldn't be rendered yet. */
  isIniting?: boolean;
  children: ReactNode;
}

export type BaseEntityViewProps<TEntity extends BaseEntityDto> = BaseEntityViewOwnProps<TEntity>;

/** Interface to be used by wrapper components that utilize this component. */
export interface BaseEntityViewInheritableProps<TEntity extends BaseEntityDto> {
  // TODO: need to refactor all entity view comps to use this
  // entityId: string | null | undefined;
  // entity: TEntity | null | undefined;
  typePlaceholder?: string;
  onUpdate?: ((newValue: TEntity | null | undefined) => void) | null | undefined;
  onDelete: (() => void) | null | undefined;
  /** Close the view (mainly for modals). */
  onClose?: (() => void) | null | undefined;
}

export default function BaseEntityView<TEntity extends BaseEntityDto = BaseEntityDto>({
  entityType,
  entityId,
  entity,
  entityRequest,
  isIniting,
  children,
}: BaseEntityViewProps<TEntity>) {
  const currentTenant = useCurrentTenant();

  const entityMeta = useMemo(
    () => EntityHelper.getEntityMeta(entity, currentTenant),
    [entity, currentTenant],
  );
  const canView = useMemo(
    () =>
      !entityMeta?.grantsMeta ||
      entityMeta.grantsMeta.isIssuedByMe ||
      (entityMeta.grantsMeta.isConsumedByMe &&
        !!currentTenant?.id &&
        !!entityMeta.grantsMeta.tenantPermissionsMap &&
        !!entityMeta.grantsMeta.tenantPermissionsMap[currentTenant.id] &&
        !!entityMeta.grantsMeta.tenantPermissionsMap[currentTenant.id][DataGrantPermission.Read]),
    [entityMeta, currentTenant],
  );

  // ensure crucial data is loaded and init is done before rendering the view
  if (isIniting) {
    return <LinearProgress />;
  }

  if (entityRequest) {
    if (entityRequest.isLoading && !entity) {
      return <LinearProgress />;
    }

    if ((entityRequest.responseDetails?.isNotFound && !entity) || (!entityId && !entity)) {
      return (
        <EntityNotFoundAlert
          title={entityRequest.responseDetails?.problemDetails?.title || undefined}
          description={entityRequest.responseDetails?.problemDetails?.detail || undefined}
        />
      );
    }

    if (
      (entityRequest.responseDetails?.isUnauthorized ||
        entityRequest.responseDetails?.isForbidden) &&
      !entity
    ) {
      return (
        <EntityForbiddenAlert
          title={entityRequest.responseDetails?.problemDetails?.title || undefined}
          description={entityRequest.responseDetails?.problemDetails?.detail || undefined}
        />
      );
    }

    if (!entity) {
      return null;
    }
  }

  return (
    <>
      {!canView && <Alert severity='error'>You are not allowed to view this entity.</Alert>}

      {canView && children}
    </>
  );
}
